import React, { FC, useState, useEffect, useMemo, Fragment } from 'react';
import clsx from 'clsx';
import { useForm, FormProvider, Controller, ControllerRenderProps, FieldValues } from 'react-hook-form';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.

import { InputTextField, Button, Types } from '@marriott/mi-ui-library';

import { useAuthStore, useLegalGenericStore, usePrivacyRightsStore } from '../../store';
import { REGEX_CONSTANTS } from '../../utils/constants';
import { usePostRequest } from '../../hooks/usePostRequest';

import { ToggleRadioButtons } from '../ToggleRadioButtons';
import { LegalTextWithLink } from '../LegalTextWithLink';

import {
  DoNotSellShareSectionProps,
  DoNotSellShareFormProps,
  PrivacyRightsFormDataType,
  PhoenixLegalUpdatePrivacyConsentResult,
} from './PrivacyRightsComponent.types';
import { PrivacyRightsModal } from './PrivacyRightsModal';
import { DONOT_SELL_SHARE_SECTION_CODE, PRIVACY_RIGHTS_API_ENDPOINT } from './constant';

const DoNotSellShareForm: FC<DoNotSellShareFormProps> = ({
  email,
  makeASelectionRadioButtonData,
  submitBtnData,
  isDoNotSellSection,
  uniqueId,
}) => {
  const { isAuthenticated: isAuth } = useAuthStore();
  const { sessionData } = useLegalGenericStore();
  const [isRadioButtonSelected, setIsRadioButtonSelected] = useState(false);
  const [showOptInConfirmationModal, setShowOptInConfirmationModal] = useState(false);
  const { DONOT_SELL_SUBMIT_SECTION_CODE, DONOT_SHARE_SUBMIT_SECTION_CODE } = DONOT_SELL_SHARE_SECTION_CODE;
  const { AUTH_SUBMIT_ENDPOINT, UNAUTH_SUBMIT_ENDPOINT } = PRIVACY_RIGHTS_API_ENDPOINT;

  const methods = useForm({
    mode: 'onSubmit',
    shouldUnregister: true,
  });

  const {
    control,
    formState: { errors },
    reset,
    setValue,
    handleSubmit,
  } = methods;

  const {
    confirmationModalGenericData,
    setDoNotSellShareFormsInputFieldsPendingDataStatus,
    setAlertComponentConfig,
    userPrivacyRightsData,
  } = usePrivacyRightsStore(state => ({
    confirmationModalGenericData: state.confirmationModalGenericData,
    setDoNotSellShareFormsInputFieldsPendingDataStatus: state.setDoNotSellShareFormsInputFieldsPendingDataStatus,
    setAlertComponentConfig: state.setAlertComponentConfig,
    userPrivacyRightsData: state.userPrivacyRightsData,
  }));

  const { optInConfirmationModalHeader, modalProceedButtonLabel, modalCancelButtonLabel } =
    confirmationModalGenericData || {};

  const [updateUserPrivacyConsent, { data: privacyConsentApiResponse, loading: isSubmitApiCallIsInProgress }] =
    usePostRequest<PhoenixLegalUpdatePrivacyConsentResult, PrivacyRightsFormDataType>({
      onError(error) {
        console.error(`Something went wrong while submitting: ${JSON.stringify(error)}`);

        setAlertComponentConfig({
          alertType: 'apiError',
          showAlertComponent: true,
        });
      },
    });

  useEffect(() => {
    if (!privacyConsentApiResponse) {
      return;
    }

    const responseCode = privacyConsentApiResponse.status?.[0]?.code;

    if (!responseCode) {
      setAlertComponentConfig({
        alertType: 'apiError',
        showAlertComponent: true,
      });
      return;
    }

    const isSuccessOrIneligible = responseCode === 'SUCCESS' || responseCode === 'INELIGIBLE';

    setAlertComponentConfig({
      alertType: isSuccessOrIneligible ? (isAuth ? 'authProfileUpdated' : 'profileUpdated') : 'profileNotFound',
      showAlertComponent: true,
    });

    if (isSuccessOrIneligible) {
      if (!isAuth) {
        reset();
        setValue('emailAddress', '');
      }
      setIsRadioButtonSelected(false);
      setDoNotSellShareFormsInputFieldsPendingDataStatus({ [uniqueId]: false });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privacyConsentApiResponse, isAuth, uniqueId]);

  useEffect(() => {
    reset();
    setDoNotSellShareFormsInputFieldsPendingDataStatus(null);

    return () => setDoNotSellShareFormsInputFieldsPendingDataStatus(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, makeASelectionRadioButtonData]);

  useEffect(() => {
    if (!isAuth || !userPrivacyRightsData) {
      return;
    }

    const optInValue = isDoNotSellSection
      ? userPrivacyRightsData.doNotSellOptIn
      : userPrivacyRightsData.doNotShareOptIn;

    setValue(makeASelectionRadioButtonData.name, makeASelectionRadioButtonData.options[optInValue ? 1 : 0].value);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, userPrivacyRightsData]);

  const { onSubmit, makeApiCall } = useMemo(() => {
    let formData: PrivacyRightsFormDataType | null = null;
    const privacyConsentSubmitApiEndPoint = isAuth ? AUTH_SUBMIT_ENDPOINT : UNAUTH_SUBMIT_ENDPOINT;

    return {
      onSubmit: async (data: Record<string, string>) => {
        const isOptIn = Object.values(data).includes('optin');
        formData = {
          input: {
            optInValue: isOptIn,
            ...(isAuth
              ? { customerId: sessionData?.cacheData?.data?.consumerID }
              : {
                  email: {
                    emailAddress: data['emailAddress'],
                  },
                }),

            consentType: isDoNotSellSection ? DONOT_SELL_SUBMIT_SECTION_CODE : DONOT_SHARE_SUBMIT_SECTION_CODE,
          },
        };

        if (isOptIn) {
          setShowOptInConfirmationModal(true);
          return;
        }

        updateUserPrivacyConsent(privacyConsentSubmitApiEndPoint, formData!, true);
      },
      makeApiCall: () => {
        setShowOptInConfirmationModal(false);
        updateUserPrivacyConsent(privacyConsentSubmitApiEndPoint, formData!, true);
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, isDoNotSellSection]);

  const closeOptInConfirmationModal = () => setShowOptInConfirmationModal(false);

  const emailChangeHandler = (field: ControllerRenderProps<FieldValues, 'emailAddress'>, value: string) => {
    field.onChange({
      target: {
        name: field.name,
        value,
      },
    });
    setDoNotSellShareFormsInputFieldsPendingDataStatus({
      [uniqueId]: isRadioButtonSelected || Boolean(value.trim()),
    });
  };

  const optInOptOutRadioButtonChangeHandler = (field: ControllerRenderProps<FieldValues, string>, value: string) => {
    setIsRadioButtonSelected(true);
    setDoNotSellShareFormsInputFieldsPendingDataStatus({
      [uniqueId]: true,
    });
    field.onChange({
      target: {
        name: field.name,
        value,
      },
    });
  };

  const showLoadingSpinnerOnButton = isSubmitApiCallIsInProgress || showOptInConfirmationModal;

  return (
    <>
      <FormProvider {...methods}>
        <form className="mb-4" onSubmit={handleSubmit(onSubmit)} data-uniqueid={uniqueId}>
          <div className="form-container mb-4">
            {!isAuth && email && (
              <div
                className={clsx('form-element', { 'unAuth-element': !isAuth })}
                data-testid={`legal-privacyRights-${isDoNotSellSection ? 'doNot-sell' : 'doNot-share'}-email`}
              >
                <h6 className="m-message-content">{email.label}</h6>
                <Controller
                  name="emailAddress"
                  control={control}
                  rules={{
                    required: email.errorMessage,
                    pattern: {
                      value: REGEX_CONSTANTS.EMAIL_REGEX,
                      message: email.errorMessage,
                    },
                  }}
                  defaultValue=""
                  render={({ field }) => (
                    <InputTextField
                      {...field}
                      className="m-input-text-field"
                      label={email.placeholder}
                      type="text"
                      testId="emailAddress"
                      inputValue={field.value}
                      showErrorMessage={!!errors?.['emailAddress']}
                      messageToShow={email.errorMessage}
                      inputMaxLength={50}
                      infoLabel="emailAddress"
                      getInputValue={value => emailChangeHandler(field, value)}
                      getInputProps={() => ({
                        autoComplete: 'off',
                        name: 'emailAddress',
                      })}
                    />
                  )}
                />
              </div>
            )}
            {makeASelectionRadioButtonData && (
              <div className={clsx('form-element', { 'unAuth-element': !isAuth })}>
                <h6 className="m-message-content">{makeASelectionRadioButtonData.label}</h6>
                <p className="mt-3">{makeASelectionRadioButtonData.makeASelectionDescriptionText}</p>
                <Controller
                  name={makeASelectionRadioButtonData.name}
                  control={control}
                  defaultValue={null}
                  render={({ field }) => (
                    <ToggleRadioButtons
                      radiobuttonName={makeASelectionRadioButtonData.name}
                      value={field.value}
                      firstRadiobuttonId={`${makeASelectionRadioButtonData.name}_${makeASelectionRadioButtonData.options[0].value}`}
                      secondRadiobuttonId={`${makeASelectionRadioButtonData.name}_${makeASelectionRadioButtonData.options[1].value}`}
                      firstRadiobuttonLabel={makeASelectionRadioButtonData.options[0].label}
                      secondRadiobuttonLabel={makeASelectionRadioButtonData.options[1].label}
                      firstRadioValue={makeASelectionRadioButtonData.options[0].value}
                      secondRadioValue={makeASelectionRadioButtonData.options[1].value}
                      firstRadioSetTabIndex={0}
                      firstRadioClassName="py-2"
                      firstRadioLabelClassName="optInOptOutRadioLabel"
                      secondRadioSetTabIndex={0}
                      firstRadioDataTestId={`${makeASelectionRadioButtonData.name}_${makeASelectionRadioButtonData.options[0].value}_testId`}
                      secondRadioDataTestId={`${makeASelectionRadioButtonData.name}_${makeASelectionRadioButtonData.options[1].value}_testId`}
                      firstRadioTabIndexForInput={0}
                      secondRadioTabIndexForInput={0}
                      firstRadioAriaLabelForLink={`${makeASelectionRadioButtonData.name}_${makeASelectionRadioButtonData.options[0].value}`}
                      secondRadioAriaLabelForLink={`${makeASelectionRadioButtonData.name}_${makeASelectionRadioButtonData.options[1].value}`}
                      secondRadioClassName="py-2"
                      secondRadioLabelClassName="optInOptOutRadioLabel"
                      onChange={value => optInOptOutRadioButtonChangeHandler(field, value)}
                    />
                  )}
                />
              </div>
            )}
          </div>

          <Button
            className={clsx('m-button-primary sbt-btn mr-4', {
              'disable-btn': !isRadioButtonSelected || showLoadingSpinnerOnButton,
            })}
            type={Types.ButtonTypeVariation.Submit}
            isDisabled={!isRadioButtonSelected || showLoadingSpinnerOnButton}
            testId={`legal-privacyRights-${isDoNotSellSection ? 'doNotSell' : 'doNotShare'}-submit-button`}
          >
            {showLoadingSpinnerOnButton ? <div className="m-spinner-s" /> : submitBtnData?.label}
          </Button>
        </form>
      </FormProvider>
      {showOptInConfirmationModal && (
        <PrivacyRightsModal
          showModal={showOptInConfirmationModal}
          labelledBy="submit-modal-title"
          onClose={closeOptInConfirmationModal}
          modalHeaderContent={optInConfirmationModalHeader!}
          modalProceedButtonLabel={modalProceedButtonLabel!}
          modalCancelButtonLabel={modalCancelButtonLabel!}
          onProceed={makeApiCall}
        />
      )}
    </>
  );
};

export const DoNotSellShareComponent: FC<DoNotSellShareSectionProps> = ({
  sectionTitle,
  sectionSubTitle,
  privacyListItems,
  isDoNotSellSection,
}) => {
  const shouldRenderDoNotSellShareForm = ({
    makeASelectionRadioButtonData,
    submitBtnData,
  }: Omit<DoNotSellShareFormProps, 'isDoNotSellSection' | 'uniqueId'>) => {
    if (!submitBtnData.label || !makeASelectionRadioButtonData.options) {
      return false;
    }

    return true;
  };

  return (
    <div className="mt-5 py-3" data-testid="legal-DoNotSellShareComponent">
      {sectionTitle && <h4>{sectionTitle}</h4>}

      {sectionSubTitle && <LegalTextWithLink descriptionText={sectionSubTitle} innerChildClassName="mt-0" />}

      {privacyListItems?.map(({ descriptionText, formData, uniqueId }) => (
        <Fragment key={uniqueId}>
          <LegalTextWithLink descriptionText={descriptionText} />
          {formData && shouldRenderDoNotSellShareForm(formData) && (
            <DoNotSellShareForm {...formData} isDoNotSellSection={isDoNotSellSection} uniqueId={uniqueId} />
          )}
        </Fragment>
      ))}
    </div>
  );
};
